import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./LoginPage.css";

// ── Asset imports ──────────────────────────────────────────────────────────
import captCrest from "../Assets/CAPT_crest.jpg";

// Slideshow images — add/remove/reorder freely here
import slideTeam      from "../Assets/slides/team_photo.jpg";
import slideDining    from "../Assets/slides/lucas.jpg";
import slideUniqlo    from "../Assets/slides/aloy.jpg";
import slideAirport   from "../Assets/slides/xianyou.jpg";
import slideFunnyFace from "../Assets/slides/kieron.jpg";
import slideSmile     from "../Assets/slides/zach.jpg";
import slideMRT       from "../Assets/slides/darren.jpg";
import slideMugshot   from "../Assets/slides/lucas_prison.jpg";
import slideHonbo     from "../Assets/slides/isaac.jpg";
import slideTibet     from "../Assets/slides/johnny.jpg";
import slideNeon      from "../Assets/slides/hayoung.jpg";
import slideFormal    from "../Assets/slides/Joel.jpg";
import slideWin       from "../Assets/slides/anson.jpg";
import slideVibing    from "../Assets/slides/zewei.jpg";

// ── Auth utilities ─────────────────────────────────────────────────────────
import { getUserIdFromName } from "../../api/announcementsAPI";
import {
  getAccess,
  clearTokens,
  fetchWithAuth,
  isValidJWTFormat,
  setRefresh,
} from "../../utils/authUtils";
import { storeEncryptedUserId } from "../../utils/cryptoUtils";

// ── Slide data ─────────────────────────────────────────────────────────────
const SLIDES = [
  {
    src: slideTeam,
    position: 'center 50%',
    eyebrow: "CAPT Nexus",
    headline: <>Built for <span>Captains</span>,<br />by Tech Comm</>,
    sub: "Your one-stop portal for announcements, bookings, IG sessions and more.",
  },
  {
    src: slideDining,
    position: 'center 40%',
    eyebrow: "CAPT Life",
    headline: <>Real <span>CAPTain</span><br />energy</>,
    sub: "From supper runs to board games — life at CAPT is never dull.",
  },
  {
    src: slideUniqlo,
    position: 'center 60%',
    eyebrow: "CAPT Vibes",
    headline: <>Always <span>ready</span><br />for anything</>,
    sub: "The tech comm that ships features AND selfies.",
  },
  {
    src: slideAirport,
    position: 'center 40%',
    eyebrow: "CAPT Moments",
    headline: <>Serious <span>on the outside</span></>,
    sub: "Out here looking like a protagonist. No notes.",
  },
  {
    src: slideFunnyFace,
    position: 'center 70%',
    eyebrow: "CAPT Faces",
    headline: <>Committed to <span>the bit</span></>,
    sub: "Every team needs that one person. This is that person.",
  },
  {
    src: slideSmile,
    position: 'center 40%',
    eyebrow: "CAPT Stories",
    headline: <>Smile <span>unlocked</span></>,
    sub: "Powered by good vibes and terrible sleeping schedules.",
  },
  {
    src: slideMRT,
    position: 'center 40%',
    eyebrow: "CAPT Transit",
    headline: <>On the MRT,<br /><span>in our feelings</span></>,
    sub: "The commute hits different when you're building cool things.",
  },
  {
    src: slideMugshot,
    position: 'center 40%',
    eyebrow: "CAPT Lore",
    headline: <>The <span>origin story</span></>,
    sub: "Every legend has a backstory. This is peak character development.",
  },
  {
    src: slideHonbo,
    position: 'center 40%',
    eyebrow: "CAPT Adventures",
    headline: <>Tourist mode:<br /><span>activated</span></>,
    sub: "When the burger mascot is taller than you.",
  },
  {
    src: slideTibet,
    position: 'center 40%',
    eyebrow: "CAPT Travels",
    headline: <>Going <span>places</span></>,
    sub: "Adventures big and small — CAPT captains go everywhere.",
  },
  {
    src: slideNeon,
    position: 'center 40%',
    eyebrow: "CAPT Creates",
    headline: <>Making things<br />with <span>meaning</span></>,
    sub: "From neon signs to next-gen portals. Creators at heart.",
  },
  {
    src: slideFormal,
    position: 'center 20%',
    eyebrow: "CAPT Formal",
    headline: <>Dressed up,<br /><span>levelled up</span></>,
    sub: "Suit up. Ship code. Repeat.",
  },
  {
    src: slideWin,
    position: 'center 40%',
    eyebrow: "CAPT Wins",
    headline: <><span>2nd place</span><br />and proud of it</>,
    sub: "S$400 richer. Hair absolutely undefeated.",
  },
  {
    src: slideVibing,
    position: 'center 10%',
    eyebrow: "CAPT Energy",
    headline: <>Just <span>vibing</span></>,
    sub: "Calm, composed, ready to build the next feature.",
  },
];

const AUTO_INTERVAL = 5000;

// ── Slideshow component ────────────────────────────────────────────────────
function Slideshow() {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);
  const total = SLIDES.length;

  const goTo = useCallback((idx) => {
    setCurrent((idx + total) % total);
  }, [total]);

  const resetAuto = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, AUTO_INTERVAL);
  }, [total]);

  useEffect(() => {
    resetAuto();
    return () => clearInterval(timerRef.current);
  }, [resetAuto]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { goTo(current + 1); resetAuto(); }
      if (e.key === "ArrowLeft")  { goTo(current - 1); resetAuto(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, goTo, resetAuto]);

  const handlePanelClick = (e) => {
    // Ripple effect
    const panel = e.currentTarget;
    const rect  = panel.getBoundingClientRect();
    const ripple = document.createElement("div");
    ripple.className = "ss-ripple";
    const size = 90;
    ripple.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - rect.left - size / 2}px;top:${e.clientY - rect.top - size / 2}px;`;
    panel.appendChild(ripple);
    ripple.addEventListener("animationend", () => ripple.remove());
    goTo(current + 1);
    resetAuto();
  };

  const handleDotClick = (e, idx) => {
    e.stopPropagation();
    goTo(idx);
    resetAuto();
  };

  return (
    <div className="ss-panel" onClick={handlePanelClick} role="region" aria-label="Photo slideshow">
      {SLIDES.map((slide, i) => (
        <div key={i} className={`ss-slide${i === current ? " ss-slide--active" : ""}`} aria-hidden={i !== current}>
          <img src={slide.src} alt={`Slide ${i + 1}`} style={{ objectPosition: slide.position }} />
          <div className="ss-overlay" />
          <div className="ss-content">
            <div className="ss-eyebrow">{slide.eyebrow}</div>
            <h2 className="ss-headline">{slide.headline}</h2>
            <p className="ss-sub">{slide.sub}</p>
          </div>
        </div>
      ))}

      <div className="ss-footer">
        <div className="ss-dots" role="tablist">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === current}
              aria-label={`Go to slide ${i + 1}`}
              className={`ss-dot${i === current ? " ss-dot--active" : ""}`}
              onClick={(e) => handleDotClick(e, i)}
            />
          ))}
        </div>
        <div className="ss-hint" aria-hidden="true">
          <span className="ss-counter">{current + 1} / {total}</span>
          <div className="ss-arrow">
            <svg viewBox="0 0 16 16" fill="none" width="11" height="11">
              <path d="M6 4L10 8L6 12" stroke="rgba(255,255,255,0.7)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Login form component ───────────────────────────────────────────────────
export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername]     = useState("");
  const [password, setPassword]     = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [errors, setErrors]         = useState({ username: "", password: "" });

  // Auto-redirect if already logged in
  useEffect(() => {
    if (getAccess()) {
      navigate("/home");
    }
  }, [navigate]);

  // ── Validation ────────────────────────────────────────────
  const validate = () => {
    const newErrors = { username: "", password: "" };
    if (!username.trim()) newErrors.username = "Please enter your username.";
    if (!password)        newErrors.password = "Please enter your password.";
    setErrors(newErrors);
    return !newErrors.username && !newErrors.password;
  };

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: "" }));

  // ── Login handler ─────────────────────────────────────────
  const handleLogin = async () => {
    if (!validate()) return;
    setLoading(true);

    try {
      const response = await fetchWithAuth(
        process.env.REACT_APP_BACKEND_URL + "/auth/login/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        }
      );

      if (!response.ok) throw new Error("Invalid credentials. Please try again.");

      const data = await response.json();

      if (!data?.access || !data?.refresh)
        throw new Error("Server did not return JWT tokens.");

      if (!isValidJWTFormat(data.access))
        throw new Error("Invalid token format.");

      sessionStorage.setItem("access", data.access);
      setRefresh(data.refresh);

      const userId = await getUserIdFromName(username);
      if (!userId) throw new Error("Failed to get user ID.");

      await storeEncryptedUserId(userId);
      navigate("/home");
    } catch (error) {
      setErrors((prev) => ({ ...prev, password: error.message }));
      clearTokens();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e, nextAction) => {
    if (e.key === "Enter") nextAction();
  };

  const contactTechComm = () => {
    const message = encodeURIComponent(
      `Hi Tech Comm Team,
    
    I'm reaching out regarding Nexus.
    
    👤 Username: ${username || "Not provided"}
    ⚠️ Error: ${errors.password || "General enquiry"}
    
    💬 Type your message here:

    
    Thanks!`
    );
  
    window.open(`https://t.me/byesaac?text=${message}`, "_blank");
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="lp-layout">
      {/* Left: Slideshow */}
      <Slideshow />

      {/* Right: Login form */}
      <div className="lp-form-panel">
        <div className="lp-wrap">

          {/* Brand */}
          <div className="lp-brand">
            <div className="lp-brand-logo">
              <div className="lp-logo-icon">
                <img src={captCrest} alt="CAPT Crest" />
              </div>
              <span className="lp-wordmark">NEXUS</span>
            </div>
            <span className="lp-brand-sub">CAPTain Portal · By Tech Comm</span>
          </div>

          {/* Card */}
          <div className="lp-card">
            <div className="lp-card-accent" />
            <div className="lp-card-header">
              <div>
                <h1 className="lp-card-title">Welcome back</h1>
                <p className="lp-card-sub">Sign in to your CAPTain account</p>
              </div>
              
            </div>

            <div className="lp-card-body">
              {/* Username */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="username">Username</label>
                <div className="lp-input-wrap">
                  <svg className="lp-input-icon" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="5.5" r="2.8" stroke="currentColor" strokeWidth="1.4" />
                    <path d="M2.5 13.5C2.5 11 5 9 8 9s5.5 2 5.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                  <input
                    id="username"
                    type="text"
                    className={`lp-input${errors.username ? " lp-input--error" : ""}`}
                    placeholder="Enter your username"
                    value={username}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    onChange={(e) => { setUsername(e.target.value); clearError("username"); }}
                    onKeyDown={(e) => handleKeyDown(e, () => document.getElementById("password").focus())}
                  />
                </div>
                {errors.username && <span className="lp-error">{errors.username}</span>}
              </div>

              {/* Password */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="password">Password</label>
                <div className="lp-input-wrap">
                  <svg className="lp-input-icon" viewBox="0 0 16 16" fill="none">
                    <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                    <path d="M5.5 7V5C5.5 3.6 6.6 2.5 8 2.5S10.5 3.6 10.5 5v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    <circle cx="8" cy="10.5" r="1" fill="currentColor" />
                  </svg>
                  <input
                    id="password"
                    type={showPw ? "text" : "password"}
                    className={`lp-input${errors.password ? " lp-input--error" : ""}`}
                    placeholder="Enter your password"
                    value={password}
                    autoComplete="current-password"
                    onChange={(e) => { setPassword(e.target.value); clearError("password"); }}
                    onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                  />
                  <button
                    type="button"
                    className="lp-pw-toggle"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? (
                      <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                        <path d="M2 2L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M6.5 6.7A2 2 0 0 0 9.3 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M4.3 4.5C3 5.3 2 6.6 2 8c0 0 2.5 4 6 4a6 6 0 0 0 3.7-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M10.5 5.5A5.9 5.9 0 0 1 14 8c0 0-2.5 4-6 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                        <ellipse cx="8" cy="8" rx="5.5" ry="3.5" stroke="currentColor" strokeWidth="1.4" />
                        <circle cx="8" cy="8" r="1.5" fill="currentColor" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <span className="lp-error">{errors.password}</span>}
              </div>

              {/* Forgot password */}
              <div className="lp-forgot-row">
                <button className="lp-forgot-link" onClick={() => navigate("./forget-password")}>
                  Forgot password?
                </button>
              </div>

              {/* Sign in */}
              <button
                className={`lp-btn-primary${loading ? " lp-btn--loading" : ""}`}
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? (
                  <span className="lp-spinner" aria-hidden="true" />
                ) : (
                  <>
                    <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                      <path d="M6 3H3V13H6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 5.5L13 8L10 10.5M13 8H6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Sign in
                  </>
                )}
              </button>

              <div className="lp-divider"><span>or</span></div>

              {/* Sign up */}
              <button className="lp-btn-secondary" onClick={() => navigate("/signup")}>
                <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                  <circle cx="7.5" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M2 13.5C2 11 4.5 9 7.5 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M11.5 10.5v4M13.5 12.5h-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                Create an account
              </button>

              <p className="lp-footer-note">
                Access is restricted to CAPT captains and committee members.
              </p>
            </div>
          </div>

          <p className="lp-below-card">
            Need help?{" "}
            <button onClick={contactTechComm}>Contact Tech Comm</button>
          </p>
        </div>
      </div>
    </div>
  );
}
